/**
 * PURPOSE: Edición inline y masiva de la programación del Centro (FTR-0028 WG3, D-24).
 * RESPONSIBILITIES: Sembrar el borrador editable, detectar qué cambió y armar los payloads de
 *   `PATCH /schedule/batch` y `PATCH /schedule/jornada`. Puro y probable con objetos planos, igual
 *   que `programacionInlineEdit.js` (FTR-0007), del que reutiliza el criterio: solo viaja lo que
 *   cambió, y "" viaja como "" para que el backend lo guarde NULL.
 *
 *   CUATRO CAMPOS, UN SOLO CAMINO. Fecha, hora, sede y cancha se editan igual desde las cuatro
 *   vistas y salen por el MISMO endpoint. El backend decide por fila si escribe `fixture_draft_match`
 *   o el partido materializado (D-08); el cliente no tiene que saberlo ni mantener dos rutas.
 *
 *   BYE NUNCA (D-20). Un BYE no se programa, no se selecciona y no entra en un lote: el backend
 *   responde `BYE_NOT_SCHEDULABLE`, y ofrecerlo sería ofrecer un error. Las filas huérfanas tampoco:
 *   su programación se guarda por si su configuración vuelve.
 *
 *   SEDE Y CANCHA VAN JUNTAS. Una cancha no sobrevive sin su sede: al limpiar la sede se limpia la
 *   cancha, y al cambiar de sede se descarta una cancha que ya no pertenece. Es la misma regla que
 *   aplica el backend, aplicada antes para no mandar un lote que se sabe inválido.
 * COLLABORATORS: programmingCenterModel.js, programmingCenterService.js, las cuatro vistas y
 *   SelectionActionBar.jsx.
 * ROUTE: winscore/src/utils/programmingCenterEdit.js
 */

/** Campos programables del Centro. Son exactamente los que acepta `pickScheduleFields`. */
export const SCHEDULE_FIELDS = ["date", "time", "idsede", "idcancha"];

/** Tope por operación del Centro (`SCHEDULE_MAX_TARGETS`). Se comprueba antes de enviar. */
export const SCHEDULE_MAX_TARGETS = 500;

const toInputValue = (field, raw) => {
  if (raw === null || raw === undefined) return "";
  if (field === "time") {
    const parsed = /^(\d{2}:\d{2})(:\d{2})?$/.exec(String(raw).trim());
    return parsed ? parsed[1] : String(raw).trim();
  }
  if (field === "idsede" || field === "idcancha") {
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? String(id) : "";
  }
  return String(raw);
};

/** Borrador inicial: `{ [iddraftmatch]: { date, time, idsede, idcancha } }` con valores de input. */
export const buildScheduleDraft = (rows = []) => {
  const draft = {};
  for (const row of rows) {
    if (!row?.iddraftmatch) continue;
    draft[row.iddraftmatch] = {
      date: toInputValue("date", row.date),
      time: toInputValue("time", row.time),
      idsede: toInputValue("idsede", row.idsede),
      idcancha: toInputValue("idcancha", row.idcancha),
    };
  }
  return draft;
};

const originalValue = (row, field) => toInputValue(field, row?.[field]);

export const isScheduleFieldDirty = (row, entry, field) => {
  if (!row || !entry || entry[field] === undefined) return false;
  return originalValue(row, field) !== toInputValue(field, entry[field]);
};

export const isRowDirty = (row, entry) =>
  SCHEDULE_FIELDS.some((field) => isScheduleFieldDirty(row, entry, field));

/**
 * Entradas sucias para el lote. Solo filas programables, solo campos cambiados. Una fila con la sede
 * limpiada arrastra su cancha aunque el usuario no la haya tocado.
 */
export const computeDirtyScheduleEntries = (rows = [], draft = {}) => {
  const entries = [];
  for (const row of rows) {
    if (!row?.iddraftmatch || !row.canSchedule) continue;
    const entry = draft[row.iddraftmatch];
    if (!entry) continue;

    const payload = { iddraftmatch: row.iddraftmatch };
    let dirty = false;
    for (const field of SCHEDULE_FIELDS) {
      if (entry[field] === undefined) continue;
      if (!isScheduleFieldDirty(row, entry, field)) continue;
      payload[field] = entry[field];
      dirty = true;
    }
    if (!dirty) continue;

    // Limpiar la sede limpia la cancha: el backend hace lo mismo, y mandar una cancha sin sede sería
    // pedir un `COURT_NOT_IN_VENUE` seguro.
    if (payload.idsede === "" && payload.idcancha === undefined && row.idcancha) {
      payload.idcancha = "";
    }
    entries.push(payload);
  }
  return entries;
};

export const hasScheduleChanges = (rows, draft) => computeDirtyScheduleEntries(rows, draft).length > 0;

/**
 * Aplica un cambio a una fila del borrador local respetando la relación sede→cancha. Devuelve una
 * fila NUEVA; no muta la entrada.
 */
export const applyScheduleChange = (entry = {}, field, value, { courtsByVenue = new Map() } = {}) => {
  const next = { ...entry, [field]: value === null || value === undefined ? "" : String(value) };
  if (field !== "idsede") return next;

  if (next.idsede === "") {
    next.idcancha = "";
    return next;
  }
  const allowed = courtsByVenue.get(Number(next.idsede)) || [];
  if (next.idcancha && !allowed.some((court) => Number(court.idcancha) === Number(next.idcancha))) {
    next.idcancha = "";
  }
  return next;
};

/** Canchas por sede, a partir del contexto. Incluye las inactivas para poder mostrarlas en gris. */
export const buildCourtsByVenue = (context = null) => {
  const map = new Map();
  for (const venue of context?.venues || []) {
    map.set(Number(venue.idsede), Array.isArray(venue.courts) ? venue.courts : []);
  }
  return map;
};

/** Solo las canchas ASIGNABLES de una sede: activas. Una retirada se ve, pero no se asigna. */
export const assignableCourts = (context, idsede) =>
  (buildCourtsByVenue(context).get(Number(idsede)) || []).filter(
    (court) => Number(court.state) === 1
  );

/**
 * Acción masiva sobre la selección: el mismo valor a todas las filas seleccionadas. Devuelve
 * `{ entries }` o `{ error }`; nunca envía un lote vacío ni uno por encima del tope.
 */
export const buildBulkScheduleEntries = ({ rows = [], selection = new Set(), changes = {} } = {}) => {
  const fields = SCHEDULE_FIELDS.filter((field) => changes[field] !== undefined);
  if (!fields.length) {
    return { error: "Indica al menos un campo (fecha, hora, sede o cancha) para aplicar." };
  }

  const targets = rows.filter((row) => selection.has(row.iddraftmatch) && row.canSchedule);
  if (!targets.length) {
    return { error: "Selecciona al menos un partido programable." };
  }
  if (targets.length > SCHEDULE_MAX_TARGETS) {
    return { error: `La selección tiene ${targets.length} partidos y el máximo por lote es ${SCHEDULE_MAX_TARGETS}.` };
  }

  const entries = targets.map((row) => {
    const payload = { iddraftmatch: row.iddraftmatch };
    for (const field of fields) payload[field] = changes[field];

    // Una cancha no sobrevive a un cambio de sede. El backend valida la cancha contra la sede
    // EFECTIVA de la fila —la nueva si se está cambiando—, así que dejar la vieja haría que el lote
    // entero fallara con COURT_NOT_IN_VENUE. Se limpia solo cuando la sede realmente cambia: volver
    // a aplicar la misma sede no debe borrar la cancha que ya estaba puesta.
    const changingVenue = payload.idsede !== undefined && payload.idcancha === undefined;
    const venueDiffers = String(payload.idsede ?? "") !== String(row.idsede ?? "");
    if (changingVenue && venueDiffers && row.idcancha) payload.idcancha = "";

    return payload;
  });
  return { entries };
};

/**
 * "Hora + N min": encadena horarios sobre la selección en su orden de presentación. Es la acción que
 * convierte "estos ocho partidos, cada 45 minutos" en un lote, sin obligar a teclear ocho horas.
 */
export const buildChainedTimeEntries = ({
  rows = [],
  selection = new Set(),
  startTime = "",
  stepMinutes = 0,
} = {}) => {
  const start = /^(\d{1,2}):(\d{2})$/.exec(String(startTime || "").trim());
  if (!start) return { error: "Indica la hora inicial en formato HH:mm." };
  const step = Number(stepMinutes);
  if (!Number.isFinite(step) || step < 0) return { error: "El intervalo debe ser un número de minutos." };

  const targets = rows.filter((row) => selection.has(row.iddraftmatch) && row.canSchedule);
  if (!targets.length) return { error: "Selecciona al menos un partido programable." };

  const base = Number(start[1]) * 60 + Number(start[2]);
  const entries = targets.map((row, position) => {
    const minutes = base + position * step;
    const hh = String(Math.floor(minutes / 60) % 24).padStart(2, "0");
    const mm = String(minutes % 60).padStart(2, "0");
    return { iddraftmatch: row.iddraftmatch, time: `${hh}:${mm}` };
  });
  return { entries };
};

/** Valor de sede que significa "sin sede". Es un bloque de programación válido, no un hueco. */
export const NO_VENUE = "none";

const clockAt = (minutes) => {
  const hh = String(Math.floor(minutes / 60) % 24).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * "Aplicar a toda la jornada": un PATCH PARCIAL sobre los partidos de una jornada. Fecha, hora y
 * sede son independientes entre sí — cada una se escribe SOLO si el operador la dio, y lo que no dio
 * se conserva. La única exigencia es que haya al menos un cambio: obligar a rellenar una hora para
 * poder mover una fecha convertía una herramienta de edición masiva en un formulario.
 *
 * LA HORA. Con duración, cada bloque de sede arranca su propia cadena en `startTime` y suma el paso
 * en el orden actual de sus filas —nunca una secuencia global que encadene una sede detrás de otra—.
 * Sin duración no hay información para incrementar nada, así que todos reciben la misma hora inicial
 * y el operador ajusta después. La duración es solo el paso de cálculo: nunca viaja como campo ni se
 * guarda en el partido. Una duración sin hora no puede secuenciar, pero tampoco bloquea los demás
 * campos: se aplican y se avisa (`warning`).
 *
 * "SIN SEDE" ES UN BLOQUE. Las filas sin sede no quedan fuera del alcance ni de la secuenciación:
 * forman su propio bloque y encadenan igual que cualquier sede. Como sede, `NO_VENUE` es el valor que
 * la quita (y con ella su cancha); `""` significa "no tocar la sede".
 *
 * EL ALCANCE es la jornada completa, resuelto sobre TODAS las filas (nunca sobre lo que el filtro de
 * pantalla deja visible), igual que `buildJornadaPayload`, para que ninguna quede fuera por oculta.
 */
export const buildJornadaEntries = ({
  rows = [],
  dateNo,
  idtypephase,
  groupNo,
  date = "",
  startTime = "",
  stepMinutes = "",
  idsede = "",
} = {}) => {
  const parsedDate = Number(dateNo);
  if (!Number.isInteger(parsedDate) || parsedDate <= 0) {
    return { error: "Indica la jornada a la que aplicar los cambios." };
  }

  const dateValue = String(date ?? "").trim();
  const timeValue = String(startTime ?? "").trim();
  const venueValue = String(idsede ?? "").trim();
  const stepValue = String(stepMinutes ?? "").trim();

  const wantsDate = dateValue !== "";
  const wantsTime = timeValue !== "";
  const wantsVenue = venueValue !== "";

  if (!wantsDate && !wantsTime && !wantsVenue) {
    // Una duración sola no es un cambio: describe un paso, no un valor que escribir.
    if (stepValue !== "") {
      return { error: "La duración necesita una hora inicial para generar una secuencia." };
    }
    return { error: "Indica al menos un campo para aplicar a la jornada." };
  }

  let base = null;
  let step = 0;
  if (wantsTime) {
    const start = /^(\d{1,2}):(\d{2})$/.exec(timeValue);
    if (!start) return { error: "Indica la hora inicial en formato HH:mm." };
    base = Number(start[1]) * 60 + Number(start[2]);

    if (stepValue !== "") {
      const parsedStep = Number(stepValue);
      if (!Number.isFinite(parsedStep) || parsedStep < 0) {
        return { error: "La duración debe ser un número de minutos." };
      }
      step = parsedStep;
    }
  }

  // Sin hora, una duración no puede secuenciar — pero los demás campos sí se aplican (§4).
  const warning =
    !wantsTime && stepValue !== ""
      ? "La duración necesita una hora inicial: se aplicaron los demás campos, pero no la secuencia."
      : null;

  const targets = rows.filter(
    (row) =>
      row.canSchedule &&
      Number(row.dateNo) === parsedDate &&
      (Number(idtypephase) > 0 ? Number(row.idtypephase) === Number(idtypephase) : true) &&
      (Number(groupNo) > 0 ? Number(row.groupNo) === Number(groupNo) : true)
  );
  if (!targets.length) {
    return { error: "Esta jornada no tiene partidos programables." };
  }
  if (targets.length > SCHEDULE_MAX_TARGETS) {
    return {
      error: `La jornada tiene ${targets.length} partidos y el máximo por lote es ${SCHEDULE_MAX_TARGETS}.`,
    };
  }

  // La sede que quedará en la fila: la nueva si se está fijando, la suya si no. Es la que decide el
  // bloque de secuenciación, porque es donde el partido se va a jugar de verdad.
  const assignedVenue = wantsVenue ? (venueValue === NO_VENUE ? "" : venueValue) : null;

  const positionByVenue = new Map();
  const entries = targets.map((row) => {
    const payload = { iddraftmatch: row.iddraftmatch };
    if (wantsDate) payload.date = dateValue;

    if (assignedVenue !== null) {
      payload.idsede = assignedVenue;
      // Una cancha no sobrevive a un cambio de sede: el backend valida la cancha contra la sede
      // EFECTIVA y dejar la vieja haría fallar el lote entero con COURT_NOT_IN_VENUE.
      if (assignedVenue !== String(row.idsede ?? "") && row.idcancha) payload.idcancha = "";
    }

    if (base !== null) {
      const effective = assignedVenue !== null ? assignedVenue : String(row.idsede ?? "");
      const venueKey = effective === "" ? NO_VENUE : Number(effective);
      const position = positionByVenue.get(venueKey) || 0;
      positionByVenue.set(venueKey, position + 1);
      payload.time = clockAt(base + position * step);
    }

    return payload;
  });

  return warning ? { entries, warning } : { entries };
};

/**
 * "Aplicar a toda la jornada N" cuando no hay selección (semántica FTR-0008): el ALCANCE lo resuelve
 * el servidor a partir de `dateNo` — el cliente no enumera filas ni puede dejarse ninguna fuera.
 */
export const buildJornadaPayload = ({ dateNo, idtypephase, groupNo, changes = {} } = {}) => {
  const parsedDate = Number(dateNo);
  if (!Number.isInteger(parsedDate) || parsedDate <= 0) {
    return { error: "Indica la jornada a la que aplicar los cambios." };
  }
  const fields = SCHEDULE_FIELDS.filter((field) => changes[field] !== undefined && changes[field] !== "");
  if (!fields.length) {
    return { error: "Indica al menos una fecha, hora, sede o cancha para aplicar a la jornada." };
  }

  const payload = { dateNo: parsedDate };
  if (Number(idtypephase) > 0) payload.idtypephase = Number(idtypephase);
  if (Number(groupNo) > 0) payload.groupNo = Number(groupNo);
  for (const field of fields) payload[field] = changes[field];
  return { payload };
};

/** Resumen humano del resultado de un lote: "17 actualizados · 2 filas del borrador" (§19). */
export const describeScheduleResult = (result = {}) => {
  const parts = [];
  const updated = Number(result.updated) || 0;
  parts.push(`${updated} ${updated === 1 ? "partido actualizado" : "partidos actualizados"}`);
  if (Number(result.draftRowsUpdated) > 0) parts.push(`${result.draftRowsUpdated} en el borrador`);
  if (Number(result.matchesUpdated) > 0) parts.push(`${result.matchesUpdated} ya materializados`);
  if (Number(result.skippedByes) > 0) parts.push(`${result.skippedByes} BYE omitidos`);
  return parts.join(" · ");
};
